import { ROUTES } from '#/config/routes';
import {
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import RepairReportStatusBadge from '#/modules/crmMgmt/aftersales/repairReport/components/RepairReportStatusBadge';
import {
  readRepairDateRangeFilter,
  resolveRepairDateFilterChipDisplay,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/repairDateListFilter';
import { REPAIR_REPORT_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/aftersales/repairReport/helper/repairReportStatus';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

export function buildRepairReportColumns({
  filters = {},
  onFilterChange,
  onRepairDateRangeChange,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.repairReportDetail(row.id),
      getEditPath: (row) => ROUTES.repairReportEdit(row.id),
      canShowEdit: (row) => row.status === 'DRAFT',
      size: 100,
      minSize: 100,
    }),
    {
      id: 'repairReportNo',
      header: '報告單號',
      accessorKey: 'repairReportNo',
      accessorFn: (row) => displayDash(row.repairReportNo),
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'repairReportNo',
        label: '報告單號',
        filterValue: filters.repairReportNo,
        onChange: (value) => onFilterChange?.('repairReportNo', value),
        placeholder: '搜尋報告單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => (
        row.original.status
          ? <RepairReportStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: REPAIR_REPORT_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange?.('status', value),
        }),
      },
    },
    {
      id: 'serialNo',
      header: '產品序號',
      accessorKey: 'serialNo',
      accessorFn: (row) => displayDash(row.serialNo),
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'serialNo',
          label: '產品序號',
          filterValue: filters.serialNo,
          onChange: (value) => onFilterChange?.('serialNo', value),
          placeholder: '搜尋序號',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'itemName',
      header: '品名',
      accessorKey: 'itemName',
      accessorFn: (row) => displayDash(row.itemName),
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'itemName',
          label: '品名',
          filterValue: filters.itemName,
          onChange: (value) => onFilterChange?.('itemName', value),
          placeholder: '搜尋品名',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'repairDate',
      header: '維修日',
      accessorFn: (row) => formatDateOrDash(row.repairDate),
      size: 120,
      minSize: 120,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'repairDateFrom',
        label: '維修日',
        mode: 'range',
        filterValue: readRepairDateRangeFilter(filters),
        onChange: onRepairDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveRepairDateFilterChipDisplay,
      }),
    },
    {
      id: 'resolution',
      header: '處理方式',
      accessorFn: (row) => displayDash(row.resolution),
      size: 200,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'resolution',
          label: '處理方式',
          filterValue: filters.resolution,
          onChange: (value) => onFilterChange?.('resolution', value),
          placeholder: '搜尋處理方式',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
  ];
}
